import React, {useEffect} from "react";
import {Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Toolbar, Typography} from "@mui/material";
import ContentLoader from "../../../../../components/content_loader";
import {Field, Form} from "react-final-form";
import TextField from "../../../../../components/text_field";
import EnhancedTable from "../../../../../components/table";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";
import {pocPrimary, pageContentGap, DialogHeader} from "../../../../../theme";
import CatalogManagerResults from "../../../../../components/CatalogManagerResults";
import {HighlightOff} from "@mui/icons-material";
import EventModal from "../../../../messages/event_modal";
import CatalogEventResults from "../../catalog/components/catalog_event_results";
import {defaultEventConfig} from "../../catalog/catalog";
import { Alert } from '@mui/material';
import {utilGetPartners} from "../../../../../utils/utils";

interface SettingProperty {
    name: string;
    label?: string;
    width?: string;
    type?: string;
    partnerOnly?: boolean;
    // Starts this property's partner-table column hidden. The column stays
    // toggleable, so the user can show it from the Columns menu.
    hidden?: boolean;
    // Predicate over the live form values plus the global settings; true
    // renders the field (and its heading/description) disabled.
    disabledWhen?: (values: any, globalSettings?: any) => boolean;
    sectionHeader?: string;
    // Builds the option list from the global settings, for a property whose
    // options describe what an unset partner is inheriting.
    optionsFrom?: (globalSettings?: any) => any[];
    // Renders this property's cell in the partner table (the row value is the
    // raw stored string, not the dialog's form value).
    cellTemplate?: (value: any, globalSettings?: any) => React.ReactNode;
    [key: string]: any;
}

interface SettingModifierState {
    refresh?: boolean;
    settings?: any;
    showConfirm?: boolean;
    disableConfirm?: boolean;
    showEdit?: boolean;
    disableEdit?: boolean;
    editRow?: any;
    eventId?: any;
    [key: string]: any;
}

interface SettingModifierProps {
    properties?: SettingProperty[];
    partnerProperties?: SettingProperty[];
    editRow: (row: any) => void;
    setState: (state: any) => void;
    getTableData: () => Promise<any>;
    system?: any;
    state: SettingModifierState;
    saveRow: (values: any) => Promise<any> | any;
    deleteRow: () => void;
    header?: React.ReactNode;
    propertiesDescription?: React.ReactNode;
    disablePartners?: boolean;
    // Clears the partner's overrides in the edit dialog's form state; the user
    // still has to Save. Rendered as an extra dialog action when supplied.
    partnerResetToGlobal?: (form: any) => void;
    loading?: boolean;
    history: any;
    location: any;
}

export const identity = (v: any) => (v);

export const checkboxTemplate = (value: any) => {
    const checked = value === 'True' || value === true || value === "true";
    return (
        <CatalogCheckbox checked={checked}/>
    );
}

export const selectTemplate = (value: any, disabled?: boolean) => {
    if(value === undefined) return (<></>);

    let valueArray: any[] = [];
    if(value === "") valueArray = [];
    else if(Array.isArray(value)) valueArray = value;
    else if(!value.includes(",")) valueArray = [value];
    else if(!Array.isArray(value)) valueArray = value.split(",");

    return (
      <Grid container style={{opacity: disabled ? 0.5 : 1}}>
          {valueArray.map((item, key) => (
                <Chip key={key} label={item}
                      variant={"outlined"}
                      style={{margin: "2px"}}
                />
          ))}
      </Grid>
    );
}

export const templateBuilder = (property: any, row: any, globalSettings?: any) => {
    if (property.cellTemplate) {
        return property.cellTemplate(row[property.name], globalSettings || {});
    }
    if (property.type === 'checkbox') {
        return checkboxTemplate(row[property.name]);
    } else if (property.type === 'select') {
        // Grey the chips when the list is not in effect for that partner, so the
        // table reads the same way the edit dialog does.
        const disabled = property.disabledWhen ? property.disabledWhen(row || {}, globalSettings || {}) : false;
        return selectTemplate(row[property.name], disabled);
    } else {
        return undefined;
    }
}

// disabledWhen, sectionHeader, optionsFrom and hidden are stripped, not
// forwarded: Field spreads unknown keys down to the component, and SwitchField
// spreads its rest onto a DOM node, so they would land on the DOM as stray
// attributes -- and `hidden` is a real HTML attribute, so forwarding it would
// make the field itself invisible instead of just its table column.
export const renderSettingField = (property: SettingProperty, values: any, globalSettings?: any) => {
    const {disabledWhen, sectionHeader, cellTemplate, optionsFrom, hidden, ...fieldProps} = property;
    const disabled = disabledWhen ? disabledWhen(values || {}, globalSettings || {}) : false;
    if(disabledWhen){
        fieldProps.disabled = disabled;
    }
    if(optionsFrom){
        fieldProps.options = optionsFrom(globalSettings || {});
    }
    return (
        <div key={property.name}>
            {sectionHeader && (
                <Typography style={{fontWeight: 'bold', fontSize: '18px', paddingBottom: '0.75em', textAlign: 'left'}}>{sectionHeader}</Typography>
            )}
            {/* Greys the whole subsection -- heading and description as well as
                the input -- so a disabled setting reads as inactive, not just
                uneditable. marginBottom keeps stacked properties from colliding. */}
            <div style={{display: 'flex', marginBottom: '1.25em', opacity: disabled ? 0.5 : 1}}>
                <Field style={{flex: 1}} parse={identity} component={TextField as any} {...fieldProps}/>
            </div>
        </div>
    );
};

const getPartners = async () => {
    const partners = await utilGetPartners();
    if (partners.errorMessage) return [];

    const sortedPartners = partners.sort((a: any, b: any) => a.CompanyName.localeCompare(b.CompanyName));
    return sortedPartners.map((r: any) => ({label: r.CompanyName, value: r.CompanyName}));
}

export default function SettingModifier(props: SettingModifierProps) {
    let properties: SettingProperty[] = props.properties || [];
    // The table config below is pinned in useState on first render, so a column
    // template that closed over props.state.settings would keep reading the
    // pre-load value. A ref stays current.
    const globalSettingsRef = React.useRef<any>(props.state.settings);
    globalSettingsRef.current = props.state.settings;
    let partnerProperties: SettingProperty[] = props.partnerProperties || properties;

    //region Dynamic Config Builder
    let columns: any[] = [
        {
            id: "AccountNumber",
            filterable: true,
            sortable: true,
            searchable: true,
            label: 'Account Number',
            width: '13em'
        },
        {
            id: 'PartnerName',
            filterable: true,
            sortable: true,
            searchable: true,
            label: 'Company Name',
            width: '13em',
            getOptions: getPartners
        },
    ];

    columns = columns.concat(partnerProperties.map(r => {
        return {
            id: r.name,
            label: r.label,
            width: r.width,
            filterable: true,
            sortable: true,
            searchable: true,
            toggleable: true,
            hidden: r.hidden,
            refresh: true,
            type: r.type === "checkbox" ? "bool" : r.type === "select" ? "array" : "text",
            getOptions: (data: any) => {
                if(!data) return null;
                if(r.type === "select") {
                    const foundFields: any[] = [];
                    data.forEach((row: any) => {
                        if(row[r.name] === undefined) return;
                        if(Array.isArray(row[r.name])) {
                            row[r.name].forEach((item: any) => {
                                foundFields.push(item);
                            });
                        } else {
                            foundFields.push(row[r.name]);
                        }
                    })

                    const uniqueFields = Array.from(new Set(foundFields));
                    return uniqueFields.map(r => ({label: r, value: r}));
                }
            },
            template: (row: any, column: any) => {
                return templateBuilder(r, row, globalSettingsRef.current);
            }
        }
    }));
    columns.push(
        {
            filterable: false,
            command: 'commands', stopPropagation: 'true', width: '20em',
            cellStyle: {padding: 0},
            template: (row: any, column: any) => {
                return (
                    <Grid container justifyContent={"flex-end"} style={{gap: "6px"}}>
                        <Button variant="outlined"
                                onClick={() => props.editRow(row)}>
                            Edit
                        </Button>
                        <Button variant="outlined"
                                onClick={() => props.setState({
                                    editRow: row,
                                    showConfirm: true,
                                    disableConfirm: false
                                })}>
                            Delete
                        </Button>
                    </Grid>
                );
            }
        }
    );

    let builtConfig = {
        savedSearchEnabled: true,
        getData: async () => {
            const results = await props.getTableData();
            if (results) {
                // Convert AccountNumber number to a string (for filters eq to work properly)
                results.forEach((r: any) => r.AccountNumber = r.AccountNumber.toString());
            }
            return results;
        },
        columns: columns,
        order: 'asc',
        orderBy: 'PartnerName',
        keyField: 'AccountNumber',
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        refresh: props.state.refresh,
        title: `Partner Specific Settings`,
        filterInUrl: false,
        filter: null,
    };
    //endregion

    let filter = {
        filters: [
            {field: "EventTypeId", operator: 'eq', value: 73, system: true},
            {field: "Category", operator: 'eq', value: props.system, system: true},
        ], logic: 'and'
    };

    const [config, setConfig] = React.useState<any>(builtConfig);
    const [eventsConfig, setEventsConfig] = React.useState<any>({
        ...defaultEventConfig,
        filter: filter,
    });

    useEffect(() => {
        setConfig((prev: any) => {
            return ({
                ...prev,
                refresh: !prev.refresh
            })
        });

        setEventsConfig((prev: any) => {
            return ({
                ...prev,
                refresh: !prev.refresh
            })
        });
    }, [props.state.refresh]);

    if (props.loading) {
        return (
            <Grid container spacing={2}>
                <Grid item size={12}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '10em', width: '100%'}}
                                   primaryColor='#e1e1e1'
                                   secondaryColor='#d8d8d8'/>
                </Grid>
                <Grid item size={12}>
                    <ContentLoader preserveAspectRatio='none' style={{height: '15em', width: '100%'}}
                                   primaryColor='#e1e1e1'
                                   secondaryColor='#d8d8d8'/>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container style={{gap: pageContentGap, flexDirection: "column", width: "100%", maxWidth: "100%"}}>
            <Grid item size={12}>
                <Card>
                    <Toolbar
                        className='lbtoolbar'
                        style={{background: pocPrimary}}
                    >
                        {props.header}
                    </Toolbar>

                    <Alert severity={"info"} style={{margin: "1em 1em 0 1em"}}>
                        <Typography variant={"body1"} style={{textAlign: "left"}}>
                            {props.propertiesDescription || "The settings configured in this section will be applied to all partners. If you would like\n" +
                                "                            to configure settings on a per-partner basis, this can be done in the Partner Specific\n" +
                                "                            space, below. Please note: any per-partner configurations will override the general settings above. "}
                        </Typography>
                    </Alert>

                    <CardContent>
                        {/* Already the default, but pinned: an unmounted field
                            must keep its value or its saved data is dropped. */}
                        <Form onSubmit={props.saveRow} initialValues={props.state.settings}
                              destroyOnUnregister={false}
                              render={({handleSubmit, pristine, hasValidationErrors, values, submitError}) => {
                                  return (
                                      <form onSubmit={handleSubmit}
                                            style={{display: 'flex', flexDirection: 'column', maxWidth: '900px'}}>
                                          {properties.map(r => renderSettingField(r, values))}
                                          {submitError && <Alert severity="error" style={{marginTop: '1em'}}>{submitError}</Alert>}
                                          <div style={{textAlign: 'left', marginBottom: '.5em', marginTop: '0.5em'}}>
                                              {/* hasValidationErrors, not invalid: final-form folds submit
                                                  errors into `invalid`, which would grey out Save on a
                                                  failed save and block the retry. */}
                                              <Button variant="contained" color="primary" disabled={pristine || hasValidationErrors} onClick={handleSubmit}>Save</Button>
                                          </div>
                                      </form>
                                  );
                              }}/>
                    </CardContent>
                </Card>
            </Grid>
            {(!props.disablePartners && partnerProperties && partnerProperties.length > 0) &&
                <Grid item size={12} style={{width: "100%"}}>
                    <EnhancedTable config={config} history={props.history} location={props.location}
                                   template={(table: any) =>
                                       <CatalogManagerResults
                                           disableTermInUrl={true}
                                           condensed={true}
                                           nonCondensedRows={true}
                                           tableHeader={"Partner Specific Settings"}
                                           history={props.history} location={props.location}
                                           table={table}
                                           config={config} setConfig={setConfig}
                                           showSavedSearch={false}
                                           savedSearchTableType="partner-specific-settings"
                                       />
                                   }
                    />
                </Grid>
            }
            <Grid item size={12} style={{width: "100%"}}>
                <EnhancedTable config={eventsConfig} history={props.history} location={props.location}
                               template={(table: any) =>
                                   <CatalogEventResults history={props.history} location={props.location}
                                                        table={table} config={eventsConfig}
                                                        setConfig={setEventsConfig}
                                                        condensed={true} tableHeader={"Events"}
                                                        showSavedSearch={false}
                                                        nonCondensedRows={true}
                                   />
                               }/>
            </Grid>
            <EventModal eventId={props.state.eventId}/>
            {/*//region Dialogs*/}
            <Dialog
                maxWidth="sm"
                aria-labelledby="confirmation-dialog-title"
                open={props.state.showConfirm}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (props.state.disableConfirm) return;
                  }
                  props.setState({showConfirm: false});
                }}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: "12px",
                        }
                    }
                }}>
                <DialogTitle>
                    <Grid item container justifyContent={"space-between"} alignItems={"center"} style={{flexDirection: "column"}}>
                        <Grid item container style={{flex: 0.5}}>
                            <DialogHeader style={{flex: 1}}>
                                Confirm Delete
                            </DialogHeader>
                        </Grid>

                        <Typography style={{fontSize: "12px", fontWeight: 300}}>
                            Are you sure you want to delete these settings?
                        </Typography>
                    </Grid>
                </DialogTitle>

                <DialogActions>
                    <Button onClick={() => props.setState({showConfirm: false})} disabled={props.state.disableConfirm}
                            color="primary">
                        No
                    </Button>
                    <Button onClick={props.deleteRow} disabled={props.state.disableConfirm} color="primary">
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                disableScrollLock
                maxWidth="sm"
                open={props.state.showEdit}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    if (props.state.disableEdit) return;
                  }
                  props.setState({showEdit: false});
                }}
                fullWidth={true}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: "12px",
                        }
                    }
                }}>
                {props.state.editRow ? (
                    <Form onSubmit={props.saveRow}
                          initialValues={props.state.editRow}
                          destroyOnUnregister={false}
                          render={({handleSubmit, form, pristine, hasValidationErrors, values, submitError}) => (
                              <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
                                  <DialogTitle>
                                      <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                                          <Grid item container style={{flex: 0.5, flexDirection: "column"}}>
                                              <DialogHeader>
                                                  Edit Settings
                                              </DialogHeader>
                                          </Grid>

                                          <IconButton onClick={() => props.setState({showEdit: false})} size="large">
                                              <HighlightOff style={{color: "red"}}/>
                                          </IconButton>
                                      </Grid>
                                  </DialogTitle>

                                  <DialogContent>
                                      <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                                          {partnerProperties.map(r => renderSettingField(r, values, props.state.settings))}
                                      </div>
                                      {submitError && <Alert severity="error" style={{marginTop: '1em'}}>{submitError}</Alert>}
                                  </DialogContent>
                                  <DialogActions>
                                      {props.partnerResetToGlobal && (
                                          <Button variant="text" color="primary"
                                                  onClick={() => props.partnerResetToGlobal!(form)}
                                                  disabled={props.state.disableEdit}>
                                              Reset
                                          </Button>
                                      )}
                                      <Button variant="text" color="primary" disabled={pristine || hasValidationErrors || props.state.disableEdit} type='submit'>Save</Button>
                                  </DialogActions>
                              </form>
                          )}/>
                ) : (
                    <DialogContent>
                        <LinearProgress style={{height: '2em'}} variant="indeterminate"/>
                    </DialogContent>
                )}
            </Dialog>
            {/*//endregion*/}
        </Grid>
    );
}
